import React, { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import TextField from "../textField"
import authService from "../../services/auth.service"
import companyService from "../../services/company.service"
import { useSelector } from "react-redux"
import dictionary from "../../utils/dictionary"
import useRecaptchaToken from "../../hooks/useRecaptchaToken"

function getPhoneValidationError(phone) {
  if (!phone || phone === "+") return null
  if (phone.startsWith("+3")) {
    if (!/^\+3\d{10}$/.test(phone) && !/^\+3\d{11}$/.test(phone)) {
      return "phone must contain 11-12 digits"
    }
    return null
  }
  if (phone.startsWith("+7")) {
    if (!/^\+7\d{10}$/.test(phone)) {
      return "phone must contain 11 digits"
    }
    return null
  }
  if (phone.length > 1) {
    return "phone must start with '7' or '3'"
  }
  return null
}

function isPhoneComplete(phone) {
  if (!phone || phone.length < 4) return false
  return !getPhoneValidationError(phone)
}

function formatRegisterError(error, lang) {
  const detail = error?.response?.data?.detail
  const code = typeof detail === "object" ? detail?.code : null
  const message =
    (typeof detail === "object" && detail?.message) ||
    (typeof detail === "string" ? detail : null) ||
    error?.message

  if (message?.includes("reCAPTCHA")) {
    return lang === "ru"
      ? "Не удалось проверить reCAPTCHA. Обновите страницу или отключите блокировщик рекламы."
      : message
  }
  if (code === "user_exists" || error?.response?.status === 409) {
    return lang === "ru"
      ? "Пользователь с таким телефоном уже зарегистрирован"
      : "User with this phone already exists"
  }
  if (code === "not_found" || error?.response?.status === 404) {
    return lang === "ru" ? "Салон не найден" : "Salon not found"
  }
  return (
    message ||
    (lang === "ru"
      ? "Не удалось зарегистрироваться. Попробуйте ещё раз."
      : "Registration failed")
  )
}

const UserRegisterForm = () => {
  const selectedLanguage = useSelector((state) => state.lang.lang)
  const t = dictionary[selectedLanguage]
  const navigate = useNavigate()
  const { required: recaptchaRequired, ready: recaptchaReady, getToken } =
    useRecaptchaToken()

  const { data: companies, isPending: salonsLoading } = useQuery({
    queryKey: ["companies", "public-list"],
    queryFn: () => companyService.listCompanies(),
  })

  const activeSalons = (companies ?? []).filter((company) => company.active !== false)

  const [companyId, setCompanyId] = useState("")
  const [data, setData] = useState({
    phone: "+",
    password: "",
  })
  const [submitError, setSubmitError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const phoneError = getPhoneValidationError(data.phone)
  const phoneComplete = isPhoneComplete(data.phone)
  const salonSelected = companyId !== ""

  const handleChange = (target) => {
    setSubmitError("")
    if (target.name === "phone") {
      if (!data.phone.startsWith("+")) {
        setData((prevState) => ({
          ...prevState,
          [target.name]: "+" + target.value,
        }))
      } else {
        setData((prevState) => ({
          ...prevState,
          [target.name]: target.value,
        }))
      }
    } else {
      setData((prevState) => ({
        ...prevState,
        [target.name]: target.value,
      }))
    }
  }

  const handleSalonChange = (event) => {
    setSubmitError("")
    setCompanyId(event.target.value)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError("")

    if (!salonSelected) {
      setSubmitError(
        selectedLanguage === "ru" ? "Выберите салон" : "Select a salon"
      )
      return
    }

    if (!phoneComplete) {
      setSubmitError(
        selectedLanguage === "ru"
          ? "Введите полный номер телефона (+7… или +381…)"
          : "Enter a complete phone number"
      )
      return
    }

    if (recaptchaRequired && !recaptchaReady) {
      setSubmitError(
        selectedLanguage === "ru"
          ? "Загружается защита от ботов. Подождите пару секунд и нажмите снова."
          : "Security check is loading. Please wait and try again."
      )
      return
    }

    setIsSubmitting(true)
    try {
      const recaptchaToken = recaptchaRequired
        ? await getToken("user_register")
        : undefined
      await authService.userRegister({
        phone: data.phone,
        password: data.password,
        companyId: Number(companyId),
        recaptchaToken,
      })
      navigate("/crm/login", { state: { registerSuccess: true } })
    } catch (error) {
      console.error(error)
      setSubmitError(formatRegisterError(error, selectedLanguage))
    } finally {
      setIsSubmitting(false)
    }
  }

  const submitDisabled =
    isSubmitting ||
    salonsLoading ||
    !activeSalons.length ||
    Boolean(phoneError) ||
    !phoneComplete ||
    !data.password ||
    !salonSelected

  return (
    <form onSubmit={handleSubmit}>
      <p className="text-brown mb-[4px] text-2xl max-md:text-lg">{t.crmRegister}</p>
      <p className="text-lightBrown mb-[24px] text-sm drop-shadow-2xl">
        {t.crmRegisterDescription}
      </p>

      <label
        className="block text-brown text-lg mb-[8px] max-md:text-sm"
        htmlFor="companyId"
      >
        {t.chooseSalon}
      </label>
      <select
        id="companyId"
        name="companyId"
        value={companyId}
        onChange={handleSalonChange}
        disabled={salonsLoading || !activeSalons.length}
        className="border rounded-lg w-full border-lightBrown py-2 px-3 text-lightBrown focus:outline-brown mb-4 disabled:opacity-60"
      >
        <option value="">
          {salonsLoading
            ? selectedLanguage === "ru"
              ? "Загрузка…"
              : "Loading…"
            : t.chooseSalonPlaceholder}
        </option>
        {activeSalons.map((company) => (
          <option key={company.id} value={company.id}>
            {company.name}
          </option>
        ))}
      </select>

      {!salonsLoading && !activeSalons.length ? (
        <p className="mb-4 text-sm text-red" role="alert">
          {t.noSalonsForRegister}
        </p>
      ) : null}

      <TextField
        name="phone"
        label={t.phoneNumber}
        placeholder="+1234567890"
        type="text"
        value={data.phone}
        onChange={handleChange}
        error={phoneError}
        disabled={!activeSalons.length}
      />
      <TextField
        name="password"
        label={t.password}
        placeholder={t.enterPassword}
        type="password"
        value={data.password}
        onChange={handleChange}
        disabled={!activeSalons.length}
      />

      {submitError ? (
        <p className="mt-3 text-sm text-red" role="alert">
          {submitError}
        </p>
      ) : null}

      {recaptchaRequired && !recaptchaReady && activeSalons.length ? (
        <p className="mt-2 text-xs text-lightBrown">
          {selectedLanguage === "ru"
            ? "Загрузка проверки безопасности…"
            : "Loading security check…"}
        </p>
      ) : null}

      <button
        className="bg-brown rounded-lg text-white text-center w-full py-[8px] mt-[24px] hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
        type="submit"
        disabled={submitDisabled}
      >
        {isSubmitting
          ? selectedLanguage === "ru"
            ? "Регистрация…"
            : "Registering…"
          : t.crmRegisterSubmit}
      </button>

      <Link to="/crm/login" className="mt-3 inline-block text-sm text-brown">
        {t.alreadyHaveAccount}
      </Link>
    </form>
  )
}

export default UserRegisterForm
